import React from 'react';
import PropTypes from 'prop-types';
import { throttle } from 'throttle-debounce';
import { inject, observer } from 'mobx-react';
import { Editor as SlateEditor } from 'slate-react';
import RangeFix from 'rangefix'; // used to fix a Safari bug

import { Toolbar } from 'libs/editor/views/toolbar';
import { MediaMenu } from 'libs/editor/views/mediaMenu';
import { AutocompleteDropdown } from 'libs/editor/views/autocomplete';
import { EditorWrapper, TextareaEditor } from 'libs/editor/styles';

import { withConditional } from 'legacyComponents/hoc/conditional';
import { FakeLink } from 'legacyComponents/styles';

const EditorToolbar = withConditional(Toolbar);
const EditorMediaMenu = withConditional(MediaMenu);
const EditorAutocomplete = withConditional(AutocompleteDropdown);

const RestoreText = withConditional(React.Fragment);

@inject('clientStore')
@observer
export class Editor extends React.Component {
  onSelectionChange = throttle(100, () => {
    const {
      store: { value, readOnly },
      store,
    } = this.props;
    if (readOnly) return;
    if (value.selection.isFocused) {
      const windowSelection = window.getSelection();
      if (windowSelection.rangeCount > 0) {
        const range = windowSelection.getRangeAt(0);
        const selection = range && RangeFix.getBoundingClientRect(range);
        store.SetSelection(selection);
      }
    }
  });

  handleInputChange = event => {
    const { store } = this.props;

    store.onChangeTextarea(event);
    this.resizeTextArea();
  };

  onResize = () => {
    const { store, clientStore } = this.props;
    if (!store.readOnly && ['xs', 's'].includes(clientStore.activeBreakPoint)) {
      store.setMarkdownValue();
    }
  };

  handleKeyDown = (event, editor, next) => {
    const { autocompleteStore, value } = this.props.store;
    const isMatching = value.selection.isFocused && autocompleteStore.visible;

    if (!isMatching && event.key === 'Enter' && !event.shiftKey) {
      this.props.onEnterKeyDown?.(event);
    }
    // prevent other listeners in the app from handling this key. (e.g. cadroom)
    event.stopPropagation();

    next();
  };

  componentDidMount() {
    const { store, clientStore } = this.props;
    if (!store.readOnly && ['xs', 's'].includes(clientStore.activeBreakPoint)) {
      store.setMarkdownValue();
    }
    window.document.addEventListener('selectionchange', this.onSelectionChange);
    window.addEventListener('resize', this.onResize);
  }

  componentWillUnmount() {
    window.document.removeEventListener(
      'selectionchange',
      this.onSelectionChange,
    );
    window.document.removeEventListener('resize', this.onResize);
  }

  componentDidCatch = (error, info) => {
    const { store } = this.props;
    store.CatchError(error, info);
  };

  resizeTextArea = () => {
    this.textAreaRef.style.height = 'auto';
    this.textAreaRef.style.height = `${this.textAreaRef.scrollHeight}px`;
  };

  setTextAreaRef = el => {
    const { store } = this.props;

    store.SetReference(el);
    this.textAreaRef = el;
  };

  render() {
    const {
      store,
      onBlur,
      onFocus,
      paragraphSpacing,
      clientStore,
      previewMode,
      previewDownloadButtonHidden,
    } = this.props;

    const {
      uuid,
      value,
      schema,
      onChange,
      readOnly,
      plugins,
      placeholder,
      spellCheck,
      hasError,
    } = store;

    const { toolbarStore, mediaMenuStore, showMediaMenu, autocompleteStore } =
      store;
    const { RestorePreviousValue, SetReference, SetComponent } = store;
    const mediaMenuSelected = mediaMenuStore && mediaMenuStore.selected;
    if (hasError) {
      return (
        <div>
          Ooops, an error related to the editor has been detected.
          <br />
          <br />
          <RestoreText condition={!this.restored}>
            You can try to recover the previous known value
            <FakeLink onClick={RestorePreviousValue}>clicking here</FakeLink>.
          </RestoreText>
        </div>
      );
    }

    // chatControl.chatVisible;
    if (!readOnly && ['xs', 's'].includes(clientStore.activeBreakPoint)) {
      // const { markdownValue = '' } = store;
      // const rows = (markdownValue.match(/\n/g) || []).length + 1;

      return (
        <EditorWrapper
          id={uuid}
          ref={SetComponent}
          className={paragraphSpacing}
        >
          {/* <TextareaEditor */}
          {/*  onChange={store.onChangeTextarea} */}
          {/*  placeholder={placeholder} */}
          {/*  value={markdownValue} */}
          {/*  rows={rows} */}
          {/* /> */}
          <TextareaEditor
            onChange={this.handleInputChange}
            placeholder={placeholder}
            ref={this.setTextAreaRef}
            // value={markdownValue}
            // rows={rows}
          />
          <EditorMediaMenu
            condition={true}
            store={store}
            style={{ marginTop: 0 }}
          />
        </EditorWrapper>
      );
    }
    return (
      <>
        <EditorWrapper
          id={uuid}
          ref={SetComponent}
          className={paragraphSpacing}
        >
          <SlateEditor
            ref={SetReference}
            value={value}
            readOnly={readOnly}
            onChange={onChange}
            onBlur={onBlur}
            onFocus={onFocus}
            onKeyDown={this.handleKeyDown}
            placeholder={readOnly || mediaMenuSelected ? '' : placeholder}
            spellCheck={spellCheck}
            plugins={plugins}
            schema={schema}
            style={this.props.style}
            previewMode={previewMode}
            previewDownloadButtonHidden={previewDownloadButtonHidden}
          />
        </EditorWrapper>
        <EditorToolbar condition={!readOnly && !!toolbarStore} store={store} />
        <EditorMediaMenu
          condition={!readOnly && showMediaMenu && !!mediaMenuStore}
          store={store}
        />
        <EditorAutocomplete
          condition={
            !readOnly && !!autocompleteStore && autocompleteStore.visible
          }
          store={store}
        />
      </>
    );
  }
}

Editor.propTypes = {
  clientStore: PropTypes.object,
  onEnterKeyDown: PropTypes.func,
  store: PropTypes.object,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  paragraphSpacing: PropTypes.string,
  style: PropTypes.object,
  previewMode: PropTypes.string,
  previewDownloadButtonHidden: PropTypes.bool,
};
